import { z } from 'zod';
import { CODE_LENGTH, CODE_ALPHABET } from './codes.js';
/** Every request body on the server is parsed by one of these. */
const codeRegex = new RegExp(`^[${CODE_ALPHABET}]{${CODE_LENGTH}}$`);
export const CodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(codeRegex, 'That code does not look right -- check it and try again.');
/**
 * Players enter their own name, so this has to cope with real ones: O'Brien,
 * Smith-Jones, Zoë, Ahmed. \p{L} covers every alphabet, and the apostrophe and
 * hyphen are allowed on purpose.
 *
 * 24 characters fits "Christopher Higginbotham" while still fitting a
 * leaderboard row on a phone; anything longer truncates in the UI anyway.
 */
export const MAX_CALL_SIGN_LENGTH = 24;
export const CallSignSchema = z
    .string()
    .trim()
    .min(2, 'A name needs at least two characters.')
    .max(MAX_CALL_SIGN_LENGTH, `${MAX_CALL_SIGN_LENGTH} characters or fewer, please.`)
    .regex(/^[\p{L}\p{N} '._-]+$/u, 'Letters, numbers, spaces, apostrophes and hyphens only.');
// --- public ---------------------------------------------------------------
export const JoinModeSchema = z.enum(['TICKET', 'CODE', 'OPEN']);
export const JoinBody = z.object({
    /** Which event to join. Omitted means "whatever the URL is currently offering". */
    eventId: z.string().min(1).optional(),
    /**
     * Absent on the practice quiz, which has no codes at all. The server decides
     * whether one was required -- a client that simply omits it does not get in to
     * a ticketed event.
     */
    code: CodeSchema.optional(),
    callSign: CallSignSchema,
});
export const CheckoutBody = z.object({
    eventId: z.string().min(1),
});
// --- player ---------------------------------------------------------------
export const AnswerBody = z.object({
    questionId: z.string().min(1),
    /**
     * Note what is NOT here: no timestamp, no latency, no elapsed time. The
     * server computes responseTimeMs as now - questionOpenedAt. A client-supplied
     * number must never reach scoring, so we do not accept one at all.
     */
    answerText: z.string().max(200),
});
export const StateQuery = z.object({
    v: z.coerce.number().int().min(0).optional(),
    /**
     * "I can render this question now" -- media downloaded, decrypted, decoded.
     *
     * Rides on the poll that is already happening rather than costing a request
     * of its own, and the client sends it once per question rather than on every
     * poll: 75 phones at 2s intervals would otherwise be 37 writes a second to
     * say nothing new.
     *
     * Advisory. It reaches the host's screen and nothing else.
     */
    ready: z.string().min(1).max(64).optional(),
});
// --- host -----------------------------------------------------------------
export const QuestionIdBody = z.object({ questionId: z.string().min(1) });
export const NextBody = z.object({
    /** Compare-and-swap guard: a double-tap must not silently skip a question. */
    expectQuestionId: z.string().min(1).optional(),
});
export const StartRoundBody = z.object({ roundId: z.string().min(1) });
export const RegradeBody = z.object({
    submissionId: z.string().min(1),
    isCorrect: z.boolean(),
});
// --- staff / door ---------------------------------------------------------
export const PenceSchema = z
    .number()
    .int('Money is always integer pence.')
    .min(0)
    .max(100_000_00);
export const CashTicketBody = z.object({
    amountPence: PenceSchema,
    note: z.string().max(200).optional(),
});
/**
 * Pre-generate a batch of codes to print or write down before the doors open.
 *
 * These are minted UNSOLD. A ticket only becomes usable once somebody records
 * that the money was taken -- a printed code that already worked would be a way
 * in for anyone who found one on the floor.
 */
export const BatchTicketsBody = z.object({
    count: z.number().int().min(1).max(100),
});
/** Record that a pre-generated code has been handed over and paid for. */
export const SellTicketBody = z.object({
    amountPence: PenceSchema,
    note: z.string().max(200).optional(),
});
export const CompTicketBody = z.object({
    reason: z.string().trim().min(1).max(200),
});
export const RefundBody = z.object({
    ticketId: z.string().min(1),
    /** Omitted means refund the full amount. */
    amountPence: PenceSchema.optional(),
});
export const RemovePlayerBody = z.object({
    /**
     * Free text rather than an enum. The reason is read by a human at the end of
     * the night trying to reconcile the takings, and "refunded, went home ill" is
     * worth more to them than a code we guessed at in advance.
     */
    reason: z.string().trim().min(1, 'Say why, so the end of the night makes sense.').max(200),
});
export const StaffCreateBody = z.object({
    displayName: z
        .string()
        .trim()
        .min(1, 'Give them a name, so the audit log reads "Dave" and not a cuid.')
        .max(40),
    role: z.enum(['HOST', 'MANAGER']),
});
export const StaffLoginBody = z.object({
    /**
     * Optional. Without it the PIN is matched across every event that is still
     * on, newest first -- because with more than one event set up there is no
     * single "current" one, and a host should not have to know an event id to
     * sign in to their own night.
     */
    eventId: z.string().min(1).optional(),
    pin: z.string().min(4).max(32),
});
// --- authoring ------------------------------------------------------------
export const QuestionFormatSchema = z.enum(['MULTIPLE_CHOICE', 'FREE_TEXT']);
export const DifficultySchema = z.enum(['EASY', 'MEDIUM', 'HARD']);
/**
 * 40 characters is the cap because "the Battle of Hastings in 1066" is not a
 * fair thing to ask somebody to type on a phone in a dark pub.
 */
export const MAX_ANSWER_LENGTH = 40;
export const QuestionCreateBody = z
    .object({
    format: QuestionFormatSchema.default('MULTIPLE_CHOICE'),
    prompt: z.string().trim().min(3).max(500),
    options: z.array(z.string().trim().min(1).max(MAX_ANSWER_LENGTH)).max(6).default([]),
    correctAnswer: z.string().trim().min(1).max(MAX_ANSWER_LENGTH),
    acceptedAnswers: z
        .array(z.string().trim().min(1).max(MAX_ANSWER_LENGTH))
        .max(10)
        .default([]),
    pointsValue: z.number().int().min(0).max(10_000).default(100),
    explanation: z.string().trim().max(500).nullish(),
    topic: z.string().trim().max(60).nullish(),
    difficulty: DifficultySchema.default('MEDIUM'),
    needsReview: z.boolean().default(false),
    /**
     * Attach a stored file. Null detaches; omitted leaves it alone, which is
     * what lets the builder save a text edit without having to resend the
     * media it is not touching.
     */
    mediaId: z.string().nullish(),
})
    .superRefine((q, ctx) => {
    if (q.format === 'MULTIPLE_CHOICE') {
        if (q.options.length < 2) {
            ctx.addIssue({
                code: 'custom',
                path: ['options'],
                message: 'A multiple-choice question needs at least two options.',
            });
        }
        // The single most common authoring mistake, caught at the door.
        if (!q.options.includes(q.correctAnswer)) {
            ctx.addIssue({
                code: 'custom',
                path: ['correctAnswer'],
                message: 'The correct answer must appear verbatim in the options.',
            });
        }
        if (new Set(q.options).size !== q.options.length) {
            ctx.addIssue({ code: 'custom', path: ['options'], message: 'Options must be distinct.' });
        }
    }
    else {
        if (q.options.length > 0) {
            ctx.addIssue({
                code: 'custom',
                path: ['options'],
                message: 'A free-text question must not carry options.',
            });
        }
        if (q.acceptedAnswers.length === 0) {
            ctx.addIssue({
                code: 'custom',
                path: ['acceptedAnswers'],
                message: 'Give at least one alternative accepted spelling.',
            });
        }
    }
});
/** PATCH takes the same shape; superRefine needs the whole object to check. */
export const QuestionUpdateBody = QuestionCreateBody;
export const QuestionQuery = z.object({
    topic: z.string().trim().max(60).optional(),
    difficulty: DifficultySchema.optional(),
    format: QuestionFormatSchema.optional(),
    /** "true" narrows the bank to what still needs checking. */
    needsReview: z.enum(['true', 'false']).optional(),
    q: z.string().trim().max(100).optional(),
    take: z.coerce.number().int().min(1).max(200).default(100),
    skip: z.coerce.number().int().min(0).default(0),
});
export const QuizCreateBody = z.object({
    name: z.string().trim().min(1).max(120),
    notes: z.string().trim().max(1000).nullish(),
});
export const RoundCreateBody = z.object({
    title: z.string().trim().min(1).max(80),
    timeLimitSec: z.number().int().min(5).max(300).nullish(),
    pointsMultiplierPct: z
        .number()
        .int('Multipliers are integer percentages -- 200 for double points, not 2.0.')
        .min(1)
        .max(1000)
        .default(100),
});
export const RoundUpdateBody = RoundCreateBody.partial();
export const ReorderBody = z.object({
    ids: z.array(z.string().min(1)).min(1).max(100),
});
export const AddQuestionToRoundBody = z.object({
    questionId: z.string().min(1),
});
export const AudioModeSchema = z.enum(['HOST', 'PLAYERS']);
export const EventCreateBody = z
    .object({
    quizId: z.string().min(1),
    name: z.string().trim().min(1).max(120),
    entryFeePence: PenceSchema.default(300),
    /**
     * TICKET: paid night, one code per player from the door.
     * CODE:   free night, one shared code on the table.
     * OPEN:   practice quiz, no code at all.
     *
     * Optional, and deliberately NOT defaulted here: leaving it out keeps the
     * older contract, where a zero fee meant a shared code and anything else
     * meant tickets. Defaulting it to TICKET would quietly turn every existing
     * free event into one nobody could get into.
     */
    joinMode: JoinModeSchema.optional(),
    timeLimitSec: z.number().int().min(5).max(300).default(20),
    /** ISO datetime. Optional: the practice quiz has no date. */
    scheduledFor: z.string().datetime().nullish(),
    /**
     * How the room hears an audio question. A property of the EVENT, not the
     * question: the same quiz runs in a venue with a PA, a venue without one,
     * and over the internet to people at home, and the question cannot know
     * which. It also decides what gets prefetched -- in HOST mode the players'
     * phones are sent no audio at all.
     */
    audioMode: AudioModeSchema.optional(),
})
    .superRefine((e, ctx) => {
    // A free way in must actually be free, or it is just a hole in the door.
    //
    // Only checked when a mode is stated: an unstated one is derived from the
    // fee downstream, so it can never contradict it.
    if (e.joinMode && e.joinMode !== 'TICKET' && e.entryFeePence !== 0) {
        ctx.addIssue({
            code: 'custom',
            path: ['entryFeePence'],
            message: 'A shared code or an open practice quiz has to be free -- otherwise it is a way past the door.',
        });
    }
});
/**
 * Editing an event after it exists.
 *
 * Name and date are always safe. The fee and the way in are not: changing them
 * once codes have been sold would rewrite what people already paid for, so the
 * server only accepts those while nothing has happened yet.
 */
export const EventUpdateBody = z.object({
    name: z.string().trim().min(1).max(120).optional(),
    scheduledFor: z.string().datetime().nullish(),
    entryFeePence: PenceSchema.optional(),
    joinMode: JoinModeSchema.optional(),
    timeLimitSec: z.number().int().min(5).max(300).optional(),
});
/** Round sizes. Five is enough to feel like a round; ten is where people fade. */
export const MIN_QUESTIONS_PER_ROUND = 5;
export const MAX_QUESTIONS_PER_ROUND = 10;
//# sourceMappingURL=schemas.js.map